import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useAppResources } from './AppResourcesContext';
import { useUserProfile } from './UserProfileContext';

const LearnedTermsContext = createContext(null);

const isObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

export const useLearnedTerms = () => {
  const context = useContext(LearnedTermsContext);
  if (!context) {
    return {
      learnedTerms: null,
      learnedTermsCount: 0,
      isLoading: false,
      setLearnedTermsData: () => {},
    };
  }
  return context;
};

export const LearnedTermsProvider = ({ children }) => {
  const { appResources, updateAppResourcesLocally } = useAppResources();
  const { isLoading: profileLoading } = useUserProfile();

  const learnedTerms = isObject(appResources?.learned_terms) ? appResources.learned_terms : null;
  const learnedTermsCount = Number.isInteger(learnedTerms?.count) ? learnedTerms.count : 0;

  const setLearnedTermsData = useCallback((updatesOrUpdater) => {
    updateAppResourcesLocally((currentResources) => {
      const currentLearnedTerms = isObject(currentResources?.learned_terms)
        ? currentResources.learned_terms
        : {};
      const updates = typeof updatesOrUpdater === 'function'
        ? updatesOrUpdater(currentLearnedTerms)
        : updatesOrUpdater;
      if (!isObject(updates)) {
        return { learned_terms: currentLearnedTerms };
      }
      const safeUpdates = { ...updates };
      const hasExistingDays = Array.isArray(currentLearnedTerms.days) && currentLearnedTerms.days.length > 0;
      const incomingDays = safeUpdates.days;
      const incomingDaysEmpty = Array.isArray(incomingDays) && incomingDays.length === 0;
      if (hasExistingDays && incomingDaysEmpty) {
        delete safeUpdates.days;
      }
      return {
        learned_terms: {
          ...currentLearnedTerms,
          ...safeUpdates,
        },
      };
    });
  }, [updateAppResourcesLocally]);

  const value = useMemo(() => ({
    learnedTerms,
    learnedTermsCount,
    isLoading: profileLoading,
    setLearnedTermsData,
  }), [
    learnedTerms,
    learnedTermsCount,
    profileLoading,
    setLearnedTermsData,
  ]);

  return (
    <LearnedTermsContext.Provider value={value}>
      {children}
    </LearnedTermsContext.Provider>
  );
};
